import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import api, { clearTokens, getAccessToken } from '../api';

interface CompanyProfile {
  income_3yrs: number;
  taxes_3yrs: number;
  pfot_3yrs: number;
  history_income_year_1?: number | null;
  history_income_year_2?: number | null;
  history_taxes_year_1?: number | null;
  history_taxes_year_2?: number | null;
  history_pfot_year_1?: number | null;
  history_pfot_year_2?: number | null;
}

export interface UserBalance {
  tracker_slots: number;
  pfu_calculations_left: number;
  vip_calculations_left: number;
  planning_calculations_left: number;
  tier_requirements_calculations_left: number;
  experience_credits?: number;
}

export interface LoginHistoryEntry {
  ip: string;
  logged_in_at: string;
}

export interface PlanServiceSnapshot {
  included_qty: number;
  discount_pct: number;
}

export interface MySubscription {
  active: boolean;
  subscription_id?: string | null;
  plan_id?: string | null;
  plan_name?: string | null;
  plan_archived?: boolean;
  status?: 'active' | 'cancelled' | 'expired' | null;
  started_at?: string | null;
  current_period_end?: string | null;
  next_billing_at?: string | null;
  ends_at?: string | null;
  price_monthly?: number;
  services?: Record<string, PlanServiceSnapshot>;
  procurement?: import('../api/procurementAdmin').ProcurementPlanConfig;
  auto_renew?: boolean;
}

interface User {
  name: string;
  surname: string;
  phone_number: string;
  whatsapp_number?: string;
  telegram_id?: string;
  auth_provider: string;
  email: string;
  role: string;
  is_active: boolean;
  is_verified: boolean;
  company_profile?: CompanyProfile | null;
  balance: UserBalance;
  login_history?: LoginHistoryEntry[];
}

interface AuthContextType {
  user: User | null;
  subscription: MySubscription | null;
  loading: boolean;
  refreshUser: () => Promise<void>;
  refreshSubscription: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({ user: null, subscription: null, loading: false, refreshUser: async () => {}, refreshSubscription: async () => {}, logout: async () => {} });

// Used only to discard stale UI when switching accounts. Server responses
// remain the authority for authentication and permissions.
function tokenSubject(token: string): string | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return typeof payload.sub === 'string' ? payload.sub : null;
  } catch { return null; }
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [subscription, setSubscription] = useState<MySubscription | null>(null);
  const [loading, setLoading] = useState(false);
  const mounted = useRef(true);
  const sessionEpoch = useRef(0);
  const userRequest = useRef(0);
  const subscriptionRequest = useRef(0);
  const loadedUser = useRef<User | null>(null);

  const invalidateReads = useCallback(() => {
    sessionEpoch.current++;
    userRequest.current++;
    subscriptionRequest.current++;
  }, []);
  const clearSession = useCallback(() => {
    invalidateReads();
    loadedUser.current = null;
    if (mounted.current) {
      setUser(null); setSubscription(null); setLoading(false);
    }
  }, [invalidateReads]);

  const refreshSubscription = useCallback(async () => {
    const token = getAccessToken();
    if (!token) { clearSession(); return; }
    const subject = tokenSubject(token);
    if (loadedUser.current && subject && loadedUser.current.email !== subject) { clearSession(); return; }
    const epoch = sessionEpoch.current;
    const request = ++subscriptionRequest.current;
    const current = () => {
      if (!mounted.current || sessionEpoch.current !== epoch || subscriptionRequest.current !== request) return false;
      const currentToken = getAccessToken(), currentSubject = currentToken ? tokenSubject(currentToken) : null;
      if (subject && currentSubject && subject !== currentSubject) { clearSession(); return false; }
      return true;
    };
    try {
      const response = await api.get('/subscriptions/me');
      if (!current()) return;
      if (!getAccessToken()) { clearSession(); return; }
      setSubscription(response.data);
    } catch (failure: unknown) {
      if (!current()) return;
      const status = (failure as { response?: { status?: number } })?.response?.status;
      if (!getAccessToken() || status === 401 || status === 403) {
        clearTokens(); clearSession();
      }
      // A transient failure does not revoke a subscription already loaded.
    }
  }, [clearSession]);

  const refreshUser = useCallback(async () => {
    const token = getAccessToken();
    if (!token) { clearSession(); return; }
    const subject = tokenSubject(token);
    if (loadedUser.current && subject && loadedUser.current.email !== subject) clearSession();
    const epoch = sessionEpoch.current;
    const request = ++userRequest.current;
    // Any subscription result started before this profile reload is stale.
    subscriptionRequest.current++;
    const current = () => {
      if (!mounted.current || sessionEpoch.current !== epoch || userRequest.current !== request) return false;
      const currentToken = getAccessToken(), currentSubject = currentToken ? tokenSubject(currentToken) : null;
      if (subject && currentSubject && subject !== currentSubject) { clearSession(); return false; }
      return true;
    };
    // Background polling must not unmount procurement gates or paid dialogs.
    setLoading(loadedUser.current === null);
    try {
      const response = await api.get('/users/me');
      if (!current()) return;
      if (!getAccessToken()) { clearSession(); return; }
      loadedUser.current = response.data;
      setUser(response.data);
      await refreshSubscription();
    } catch (failure: unknown) {
      if (!current()) return;
      const status = (failure as { response?: { status?: number } })?.response?.status;
      if (!getAccessToken() || status === 401 || status === 403) {
        clearTokens(); clearSession();
      }
      // Telegram polling and post-payment refreshes must retain the current
      // profile on network/5xx errors instead of showing a false logout.
    } finally {
      if (current()) setLoading(false);
    }
  }, [refreshSubscription, clearSession]);

  const logout = useCallback(async () => {
    // Invalidate reads immediately. The cookie-only logout endpoint does not
    // require the access token, and late reads must not revive this session.
    clearTokens(); clearSession();
    try {
      await api.post('/auth/logout');
    } catch {
      // Ignore server errors — still clear locally
    }
  }, [clearSession]);

  useEffect(() => {
    mounted.current = true;
    void refreshUser();
    return () => {
      mounted.current = false;
      invalidateReads();
    };
  }, [refreshUser, invalidateReads]);

  return (
    <AuthContext.Provider value={{ user, subscription, loading, refreshUser, refreshSubscription, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
